import { humanizeListingApiError } from '@/utils/humanizeListingApiError';

type LaravelLikeBody = {
  message?: string;
  errors?: Record<string, string[] | string>;
};

const normalizeDatabaseErrorMessage = (message: string): string => {
  if (/Duplicate entry .*products_seller_sku_unique/i.test(message)) {
    return 'SKU already exists for your listings. Please use a unique SKU.';
  }

  return message;
};

const getMessageForKey = (
  errors: Record<string, string[] | string>,
  key: string
): string | undefined => {
  const val = errors[key];
  if (Array.isArray(val) && val.length > 0) return String(val[0]);
  if (typeof val === 'string' && val.trim()) return val.trim();
  return undefined;
};

const firstErrorEntryFromMap = (
  errors: Record<string, string[] | string>
): { key: string; message: string } | undefined => {
  const firstKey = Object.keys(errors)[0];
  if (!firstKey) return undefined;
  const msg = getMessageForKey(errors, firstKey);
  if (msg) return { key: firstKey, message: msg };
  return undefined;
};

/** Prefer cart/variant field errors so users see actionable messages (add/update/remove). */
const pickPreferredFieldErrorEntry = (
  errors: Record<string, string[] | string>
): { key: string; message: string } | undefined => {
  const preferredKeys = ['status', 'product_variant_id', 'quantity', 'product_id'];
  for (const key of preferredKeys) {
    const directMsg = getMessageForKey(errors, key);
    if (directMsg) return { key, message: directMsg };

    const nested = Object.keys(errors).find((k) => k === key || k.endsWith(`.${key}`));
    if (nested) {
      const msg = getMessageForKey(errors, nested);
      if (msg) return { key: nested, message: msg };
    }
  }
  return undefined;
};

/**
 * Normalizes Laravel-style JSON errors and generic fetch errors into a single user-facing string.
 */
export function getApiErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'data' in error) {
    const data = (error as { data?: LaravelLikeBody }).data;
    if (data && typeof data === 'object') {
      if (data.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)) {
        const entry = pickPreferredFieldErrorEntry(data.errors) ?? firstErrorEntryFromMap(data.errors);
        if (entry) {
          return normalizeDatabaseErrorMessage(
            humanizeListingApiError(entry.key, entry.message)
          );
        }
      }
      if (typeof data.message === 'string' && data.message.trim()) {
        return normalizeDatabaseErrorMessage(humanizeListingApiError(undefined, data.message));
      }
    }
  }

  if (error && typeof error === 'object' && 'message' in error) {
    const msg = (error as { message?: string }).message;
    if (typeof msg === 'string' && msg.trim())
      return normalizeDatabaseErrorMessage(humanizeListingApiError(undefined, msg));
  }

  return 'Something went wrong';
}
